/**
 * The two pictures an event has: its own photograph, and a film's poster.
 *
 * This file was the nine card shapes, from Doug's prototype "One feed, nine
 * card shapes" — `SwipeRow` owned the gesture and everything between the title
 * and the buttons was here. WP 2.4 replaced all nine with one row (`EventRow`,
 * over `ui/Row`): the shapes each answered "what does this kind of event need
 * said", and the answer turned out to be the same three columns every time,
 * with the differences belonging in one meta line rather than in nine layouts.
 * What is left is the two components that draw a picture, because both of them
 * hold the fallback state an `onError` needs and neither is a layout.
 *
 * `runProgress` and `rhythmOf` went to `app/src/lib/rowMeta.ts` and
 * `cardShape.ts` is gone; `docs/archive/` has the shapes if the reasoning is
 * ever wanted again, and the git history has the code.
 */
import { useState } from 'react'
import type { FeedRow } from '../lib/types'
import {
  heroAlt, heroCredit, heroFallbackUrl, heroIsVenue, heroSrcSet, heroUrl, posterUrl, swatch,
} from '../lib/entries'

export interface ShowingLite {
  entry_id: string
  starts_at: string
  venue: string | null
  district: string | null
  /** The building, for counting cinemas rather than counting halls. */
  building_id?: string | null
  venue_id?: string | null
  /** OV/OmU/OmeU/DF, as the house prints it on the showing. Phase 6c. */
  note?: string | null
  /** The showing's own venue, joined client-side from the feed rows that
   *  share it — `showing_list` carries no coordinates of its own. Phase 6c. */
  lat?: number | null
  lng?: number | null
  /** What the row is — a performance, an opening, a run marker, an
   *  annotation. Phase 7b; Phase 9's `ShowingsList` is the first reader on
   *  the app side. */
  kind?: string | null
  /** A per-showing ticket link, where it differs from the entry's own.
   *  Phase 9. */
  url?: string | null
}

/** The picture, with the venue's name over it when the picture is of the
 *  venue. A photograph of the Philharmonie above a Bruckner programme is
 *  scene-setting; unlabelled it would read as a photograph of the concert. */
export function Hero({ e, ratio, onOpen }: { e: FeedRow; ratio: string; onOpen: () => void }) {
  // Above the guard below, on purpose — a hook after an early return does
  // not run on every render, and this one has to run on every render of
  // every Hero, whether or not it ends up with a picture to draw.
  //
  // 0 = the rendition `heroUrl` asked for, 1 = the failed rendition
  // 404s so a browser's own retry falls back to the original (rule 2: the
  // reader never assumes a rendition exists), 2 = that failed too and this
  // is no picture, same as none. Stage, not a URL, is what is stateful —
  // recomputing the URLs from `e` on every render is what keeps a saved or
  // dismissed row's own re-render from showing a stale src.
  const [stage, setStage] = useState<0 | 1 | 2>(0)
  const primary = heroUrl(e, 800)

  // No photograph, no image slot. The prototype's own lede says it: "Images
  // stand in for one hero per venue — where there is none, the date takes its
  // place." A 4:3 colour swatch across the full width of an exhibition card is
  // 300 pixels of nothing, and no gallery has a hero: the seven that exist are
  // concert halls, cinemas and theatres. The swatch stays where it is small
  // enough to be a marker rather than a void — see the Film poster.
  if (!primary) return null

  const credit = heroCredit(e)
  const src = stage === 0 ? primary : stage === 1 ? heroFallbackUrl(e) : null
  // A picture that fails to load twice is the same as no picture: the
  // swatch stays, the browser's own broken-image icon and alt text do not
  // draw over it (UI-audit F12). `stage` is not re-checked on the next row
  // this component draws for — it is local to this mounted instance, reset
  // by the row's own key when the row underneath it changes.
  const showImg = !!src
  const [rw, rh] = ratio.split('/').map(Number)
  const width = 680
  const height = Math.round((width * rh) / rw)

  return (
    <>
      <button className="media" style={{ aspectRatio: ratio }} onClick={onOpen} aria-label={e.title}>
        <span className="ph" style={{ background: swatch(e.id) }} />
        {showImg && (
          <img
            className="ph" src={src!}
            srcSet={stage === 0 ? (heroSrcSet(e) ?? undefined) : undefined}
            sizes="(max-width: 680px) 100vw, 680px"
            width={width} height={height}
            alt={heroAlt(e)} loading="lazy" decoding="async"
            onError={() => setStage(s => (s === 0 ? 1 : 2))}
          />
        )}
        {/* The building, not the hall: the venue line below already says
            "Philharmonie Berlin, Grosser Saal", and repeating it over the
            picture is the same sentence twice. */}
        {showImg && heroIsVenue(e) && <span className="vlabel">{e.building ?? e.venue}</span>}
      </button>
      {/* Attribution is a condition of showing the picture, not a nicety —
          the reasoning at the top of src/harvest/venuehero.ts. An
          exhibition's press image carries a courtesy line and a venue
          photograph carries an author and a licence; `heroCredit` returns
          whichever belongs to the picture actually drawn above. */}
      {showImg && credit && <p className="imgcredit">{credit}</p>}
    </>
  )
}

/**
 * A film's poster, and never a photograph of the cinema.
 *
 * Split out from the Film shape for the same reason `Hero` is its own
 * component: a poster URL that 404s or times out (TMDb, hotlinked) drew the
 * browser's broken-image icon and its alt text over the swatch that was
 * supposed to be the fallback (UI-audit F12), and `onError` needs somewhere of
 * its own to keep the failed state in.
 *
 * **It used to fall through to the venue's photograph** — `heroUrl` goes
 * poster, then the row's own picture, then `venue_hero_key` — so a Film or
 * Release row with no poster showed a picture of a cinema foyer in a 2:3 box,
 * with no label saying it was the building and no credit for a Commons
 * photograph that requires one (`NEXT.md`'s open item, fixed here because this
 * package rebuilt everything around this component). The fix is to drop the
 * fallback rather than to label it: D7 says a venue photograph never stands in
 * for a picture of the event, and at a poster's size there is nowhere to put
 * the qualification that would make it honest. No poster means the swatch,
 * which is a marker rather than a void.
 */
export function FilmPoster({ e, onOpen }: { e: FeedRow; onOpen: () => void }) {
  // See `Hero`'s own comment for what the three stages mean.
  const [stage, setStage] = useState<0 | 1 | 2>(0)
  // `heroIsVenue` answers "is the row's *hero* the building's photograph", and
  // it says yes for a film at a cinema that has one — it does not know about
  // the poster, which `heroUrl` returns ahead of everything else. Asking it
  // first therefore threw away the poster of every film at a venue with a
  // photograph, which is most of them; the guard is only needed where there is
  // no poster to prefer.
  const isVenuePicture = !posterUrl(e) && heroIsVenue(e)
  const primary = isVenuePicture ? null : heroUrl(e, 800)
  const src = stage === 0 ? primary : stage === 1 && !isVenuePicture ? heroFallbackUrl(e) : null
  const showImg = !!src
  const credit = heroCredit(e)
  return (
    <>
      <button className="media" style={{ aspectRatio: '2 / 3' }} onClick={onOpen} aria-label={e.title}>
        <span className="ph" style={{ background: swatch(e.id) }} />
        {showImg && (
          <img
            className="ph" src={src!}
            srcSet={stage === 0 ? (heroSrcSet(e) ?? undefined) : undefined}
            sizes="158px" width={158} height={237}
            alt={heroAlt(e)} loading="lazy" decoding="async"
            onError={() => setStage(s => (s === 0 ? 1 : 2))}
          />
        )}
      </button>
      {/* The same rule as `Hero`: attribution is a condition of showing the
          picture, not a nicety. TMDb asks for it on a poster and a Commons
          photograph's licence requires it. */}
      {showImg && credit && <p className="imgcredit">{credit}</p>}
    </>
  )
}
